import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';

interface UseTeamAuthCheckProps {
  teamId?: number;
}

export const useTeamAuthCheck = ({ teamId }: UseTeamAuthCheckProps) => {
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [loading, setLoading] = useState(true);
  const { currentUser, permissions } = useUserRole();

  useEffect(() => {
    const checkTeamAuth = async () => {
      if (!currentUser || !teamId) {
        setCanManageTeam(false);
        setLoading(false);
        return;
      }

      try {
        // Check if user is team owner
        const { data: teamData } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', teamId)
          .single();

        if (teamData?.created_by === currentUser.id) {
          setCanManageTeam(true);
          setLoading(false);
          return;
        }

        // Check if user is a coach of this team
        const { data: coachData } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', teamId)
          .eq('coach_id', currentUser.id)
          .single();

        setCanManageTeam(!!coachData);
      } catch (error) {
        console.error('Error checking team authorization:', error);
        setCanManageTeam(false);
      } finally {
        setLoading(false);
      }
    };

    checkTeamAuth();
  }, [currentUser, teamId]);

  return {
    canManageTeam,
    loading,
    isTeamOwner: permissions.isTeamOwner,
    isCoach: permissions.isCoach
  };
};